import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import usePageTitle from "../hooks/usePageTitle";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

const LoginPage = () => {
  usePageTitle('Login', 'Sign in to your Advanta Plus membership account');
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember_me: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password, formData.remember_me);
      toast.success("Welcome back!");
      
      if (user.role === "admin" || user.role === "team_member" || user.role === "territory_owner") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6 relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1613429561582-2b20fc0028dd?w=1920')", 
          backgroundSize: "cover", 
          backgroundPosition: "center" 
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#020617] via-[#020617]/90 to-[#020617]" />
      
      {/* Back Button */}
      <Link 
        to="/"
        className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors z-10"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>{t('common.back')}</span>
      </Link>
      
      {/* Form Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="glass-dark rounded-2xl p-8 shadow-luxury">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
                <span className="text-[#020617] font-bold text-xl tracking-tight">A+</span>
              </div>
            </Link>
            <h1 className="font-['Playfair_Display'] text-3xl text-white mt-4">{t('auth.welcome_back')}</h1>
            <p className="text-white/50 mt-2">{t('auth.sign_in_desc')}</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.remember_me}
                  onChange={(e) => setFormData({ ...formData, remember_me: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]"
                />
                <span className="text-white/60 text-sm">Stay logged in</span>
              </label>
              <Link to="/forgot-password" className="text-white/50 hover:text-[#D4AF37] text-sm">
                {t('auth.forgot_password')}
              </Link>
            </div>

            <Button
              type="submit"
              className="btn-gold w-full py-6 rounded text-base"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.sign_in')}
            </Button>
          </form>
          
          <div className="mt-8 text-center">
            <p className="text-white/50">
              {t('auth.no_account')}{" "}
              <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="register-link">
                {t('auth.create_one')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
